import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {sum, isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import {getPercent} from '@ey/utils';
import Utility from '../../../../../../util/utilityfunctions';
import {labels} from '../../../../../../util/utils';
import normalizeDashboardFilters from '../../../../../../util/dashboard/normalizeDashboardFilters';
import {numberFormatsListItems, GARoleTypeId, dashboardTabId} from '../../../../../../util/uiconstants';
import SummaryBarChart from '../../../../../Common/Charts/SummaryBarChart';
import {
	clearFilterDashboardTiles,
	updateFilterDashboardTiles
} from '../../../../../../actions/Metrics/dashboardFiltersAction';
import summaryStatusTileComponentDataConfig from './summaryStatusTileComponentDataConfig';

const ComponentTeamSummaryStatus = ({engagementMetrics}) => {
	const dispatch = useDispatch();
	const [summaryStatusTileData, setSummaryStatusTileData] = useState({});
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const isGroupAuditComponentTileEnabled = useSelector(
		(store) => store.permissions?.configurationSettings?.features?.groupAuditComponentStatusTileEnable
	);

	useEffect(() => {
		if (!isEmpty(engagementMetrics) && isGroupAuditComponentTileEnabled == true) {
			let _engagementMetrics = engagementMetrics?.filter(
				(x) => x?.gAType === GARoleTypeId.Component || x?.gAType === GARoleTypeId.Region
			);

			// sum all values to get the total for the selected engagements
			const total = sum(_engagementMetrics.map((item) => item.cTTotal));
			const taskNotSentOverdue = sum(_engagementMetrics.map((item) => item.cTNSOD));
			const taskReturnedOverdue = sum(_engagementMetrics.map((item) => item.cTROD));
			const taskUnlinked = sum(_engagementMetrics.map((item) => item.cTUnlinkTot));
			const taskNotSent = sum(_engagementMetrics.map((item) => item.cTNotSent));
			const taskReturned = sum(_engagementMetrics.map((item) => item.cTRetrn));
			const taskPartialSend = sum(_engagementMetrics.map((item) => item.cTPartialSend));
			const taskCompleteSend = sum(_engagementMetrics.map((item) => item.cTComplSend));

			const [
				{percentage: taskNotSentPercent},
				{percentage: taskReturnedPercent},
				{percentage: taskPartialSendPercent},
				{percentage: taskCompleteSendPercent}
			] = Utility.adjustPercentages([
				{percentage: getPercent(taskNotSent, total)},
				{percentage: getPercent(taskReturned, total)},
				{percentage: getPercent(taskPartialSend, total)},
				{percentage: getPercent(taskCompleteSend, total)}
			]);
			const formatId = numberFormat && numberFormatsListItems.find((x) => x.label == numberFormat)?.id;

			setSummaryStatusTileData(
				summaryStatusTileComponentDataConfig(
					formatId,
					total,
					numberFormat,
					taskNotSent,
					taskNotSentOverdue,
					taskNotSentPercent,
					taskReturned,
					taskReturnedOverdue,
					taskReturnedPercent,
					taskPartialSend,
					taskPartialSendPercent,
					taskCompleteSend,
					taskCompleteSendPercent,
					taskUnlinked
				)
			);
		} else {
			setSummaryStatusTileData({});
		}
	}, [engagementMetrics]);

	const getUnlinkedCount = () => {
		const unlinkLabel = summaryStatusTileData.taskUnlinked === 1 ? labels.unlinkedTask : labels.unlinkedTasks;

		return [
			summaryStatusTileData.taskUnlinked &&
				Utility.getFormattedNumber(numberFormat, summaryStatusTileData.taskUnlinked),
			unlinkLabel,
			() => onGraphicClickHandler(unlinkLabel)
		];
	};

	const onGraphicClickHandler = (serieLabel) => {
		updateFilters(serieLabel);
	};

	const updateFilters = (serieLabel) => {
		const filter = [
			{
				categoryLabel: isGroupAuditComponentTileEnabled == false ? labels.comingsoonTaskStatus : labels.componentTask,
				serieLabel: serieLabel,
				onClearChip: handlerOnClearFilter
			}
		];

		dispatch(
			updateFilterDashboardTiles(
				dashboardTabId.groupAudit,
				normalizeDashboardFilters(
					filter,
					engagementMetrics?.filter((eng) => filterEngagementByChips(eng, filter)).map((e) => e.engId) ?? [],
					handlerOnClearFilter
				)
			)
		);
	};

	const filterEngagementByChips = (engagement, filter) => {
		if (!engagement || _.isEmpty(engagement)) return false;
		return filter.some(({serieLabel}) => {
			return evalComponentTaskStatus(engagement, serieLabel) || false;
		});
	};

	const evalComponentTaskStatus = (engagement, serieLabel) => {
		const {cTComplSend, cTNotSent, cTPartialSend, cTRetrn, cTUnlinkTot} = engagement;
		switch (serieLabel) {
			case labels.groupAuditNotSent:
				return cTNotSent;
			case labels.returned:
				return cTRetrn;
			case labels.evidencePending:
				return cTPartialSend;
			case labels.evidenceComplete:
				return cTComplSend;
			case labels.unlinkedTask:
				return cTUnlinkTot;
			case labels.unlinkedTasks:
				return cTUnlinkTot;
			default:
				return false;
		}
	};

	const handlerOnClearFilter = () => dispatch(clearFilterDashboardTiles(dashboardTabId.groupAudit));

	return (
		<SummaryBarChart
			title={isGroupAuditComponentTileEnabled == false ? labels.comingsoonTaskStatus : labels.componentTaskStatus}
			secondTitle={isGroupAuditComponentTileEnabled == false ? '' : labels.summarizedForComponentTeams}
			count={summaryStatusTileData.total}
			subtitle={labels.componentTileSubtitle}
			chartsConfig={summaryStatusTileData.chartsData}
			noDataTitle={
				isGroupAuditComponentTileEnabled == false ? labels.comingSoonToDisplay : labels.thereIsNoDataToDisplay
			}
			noDataSubtitle={
				isGroupAuditComponentTileEnabled == false
					? labels.checkForUpdatesToDisplay
					: labels.selectadditionalEntitiesToContinue
			}
			footer={getUnlinkedCount()}
			numberFormat={numberFormat}
			onGraphicClick={onGraphicClickHandler}
		/>
	);
};

ComponentTeamSummaryStatus.defaultProps = {
	engagementMetrics: []
};

ComponentTeamSummaryStatus.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default ComponentTeamSummaryStatus;
